import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { CircularProgress, styled, Box } from '@mui/material';
import { LibraryCard } from 'components/LibraryCard/LibraryCard';
import { SearchBar } from 'components/SearchBar/SearchBar';
import { useDebouncedValue } from 'hooks/useDebouncedvalue';
import { RetryButton } from 'components/RetryButton/RetryButton';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { TransformedLibraryArticle } from 'features/resources/types';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { PendoEventType } from 'features/pendo/types';
import { ResourceCardGrid, ResourceCardGridCell } from './ResourceCardGrid';
import { ResourceSection } from './ResourceSection';
import { ResourceSearchErrorState } from './ResourceSearchErrorState';
import { ResourceNoArticlesState } from './ResourceNoArticlesState';
import { ContentSearchResults } from './ContentSearchResults';
import { ResourceDesktopFavourites } from './ResourceDesktopFavourites';

const SEARCH_DEBOUNCE_MS = 500;

// Mobile shows a capped preview with a "View all" link; tablet/desktop render every article.
const MOBILE_ARTICLE_LIMIT = 4;

const InfiniteScrollContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2, 0),
}));

// From Figma: search bar is 270px wide on tablet/desktop.
const SearchBarWrapper = styled(Box)(() => ({
  width: 270,
  flexShrink: 0,
  marginRight: 24,
}));

interface ResourceLibraryArticleSectionProps {
  libraryArticles?: TransformedLibraryArticle[];
  /** Currently favorited articles; may include ones outside `libraryArticles` (e.g. favorited from search). */
  favoritedArticles?: TransformedLibraryArticle[];
  handleArticleClick?: (article: TransformedLibraryArticle) => void;
  isResourceError?: boolean;
  isArticleFavorited: (article: TransformedLibraryArticle) => boolean;
  onToggleFavorite: (article: TransformedLibraryArticle) => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  isLoadMoreError?: boolean;
  onRetryLoadMore?: () => void;
}

export const ResourceLibraryArticleSection = ({
  libraryArticles = [],
  favoritedArticles = [],
  handleArticleClick,
  isResourceError = false,
  isArticleFavorited,
  onToggleFavorite,
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
  isLoadMoreError = false,
  onRetryLoadMore,
}: ResourceLibraryArticleSectionProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { isMobile, isDesktop, isTablet } = useDeviceType();
  const { translation } = useTranslationContext();
  const navigate = useNavigate();
  const { trackEvent } = usePendo();

  const sentinelRef = useInfiniteScroll(onLoadMore);

  const showViewAll = useMemo(() => libraryArticles.length > 0, [libraryArticles]);

  // isSearching switches views immediately (e.g. on clear); the debounced term drives the API call.
  const isSearching = searchTerm.trim().length > 0;
  const { debouncedValue: debouncedSearchTerm, flush: flushSearchDebounce } = useDebouncedValue(
    searchTerm,
    SEARCH_DEBOUNCE_MS,
  );
  const trimmedSearchTerm = debouncedSearchTerm.trim();

  const displayedArticles = useMemo(
    () => (isMobile ? libraryArticles.slice(0, MOBILE_ARTICLE_LIMIT) : libraryArticles),
    [isMobile, libraryArticles],
  );

  const showFavoritesGroup =
    (isDesktop || isTablet) && !isSearching && !isResourceError && favoritedArticles.length > 0;

  const handleViewAll = () => {
    trackEvent(PendoEventType.ALL_ARTICLES, {
      eventType: PendoEventType.ALL_ARTICLES,
    });

    navigate('library');
  };

  const handleSearchSubmit = (keyword: string) => {
    const searchKeyword = keyword.trim();
    if (searchKeyword) {
      trackEvent(PendoEventType.SEARCH_KEYWORD_ARTICLE, {
        eventType: PendoEventType.SEARCH_KEYWORD_ARTICLE,
        searchKeyword,
      });
    }

    flushSearchDebounce();
  };

  const renderContent = () => {
    if (isSearching) {
      return (
        <ContentSearchResults
          searchTerm={trimmedSearchTerm}
          onArticleClick={handleArticleClick}
          isArticleFavorited={isArticleFavorited}
          onToggleFavorite={onToggleFavorite}
        />
      );
    }

    if (isResourceError) {
      return <ResourceSearchErrorState />;
    }

    if (libraryArticles.length === 0) {
      return <ResourceNoArticlesState />;
    }

    return (
      <>
        <ResourceCardGrid>
          {displayedArticles.map((article, index) => (
            <ResourceCardGridCell key={`${article.id}_${index}`}>
              <LibraryCard
                title={article.title}
                imageUrl={article.imageUrl}
                onClick={() => handleArticleClick?.(article)}
                ariaLabel={`${article.title} article`}
                showFavorite
                isFavorited={isArticleFavorited(article)}
                onToggleFavorite={() => onToggleFavorite(article)}
              />
            </ResourceCardGridCell>
          ))}
        </ResourceCardGrid>
        {!isMobile && hasMore && onLoadMore && !isLoadMoreError && (
          <InfiniteScrollContainer ref={sentinelRef}>
            {isFetchingMore && <CircularProgress size={28} />}
          </InfiniteScrollContainer>
        )}
        {!isMobile && isLoadMoreError && onRetryLoadMore && (
          <InfiniteScrollContainer>
            <RetryButton onClick={onRetryLoadMore} />
          </InfiniteScrollContainer>
        )}
      </>
    );
  };

  return (
    <ResourceSection
      description={translation('resources_library_subheader')}
      title={translation('resources_library_header')}
      titleId="resources_library_header"
      viewAllText={translation('member_resources_view_all_articles_button')}
      viewAllAriaLabel={`${translation('member_resources_view_all_articles_button')} - ${translation(
        'resources_library_header',
      )}`}
      onViewAll={handleViewAll}
      headerAction={
        !isMobile ? (
          <SearchBarWrapper>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              onSubmit={handleSearchSubmit}
              placeholder={translation('search_bar_copy')}
            />
          </SearchBarWrapper>
        ) : undefined
      }
      showViewAll={showViewAll}
    >
      {showFavoritesGroup && (
        <ResourceDesktopFavourites
          favoritedArticles={favoritedArticles}
          allArticlesLabel={translation('all_articles')}
          handleArticleClick={handleArticleClick}
          onToggleFavorite={onToggleFavorite}
        />
      )}
      {renderContent()}
    </ResourceSection>
  );
};
